import { useState } from "react";
import { publicFileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { QrCode, Download, MessageCircle, Maximize2, Landmark } from "lucide-react";

export default function PaymentSection({ settings }) {
  const [zoom, setZoom] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waName, setWaName] = useState("");
  const [waAmount, setWaAmount] = useState("");
  const [waDate, setWaDate] = useState("");
  const [waMethod, setWaMethod] = useState("QRIS");

  if (!settings) return null;
  const hasQris = !!settings.qris_file_id;
  const hasWa = !!(settings.pic_whatsapp && settings.pic_whatsapp.length > 0);
  const hasBank = !!(settings.bank_info && settings.bank_info.trim());
  const hasInstructions = !!(settings.payment_instructions && settings.payment_instructions.trim());

  if (!hasQris && !hasWa && !hasBank && !hasInstructions) {
    return (
      <div className="p-5 rounded-lg bg-[#FBF5E8] border border-[#C59B27]/30 text-sm text-[#64748B]" data-testid="payment-empty">
        Informasi pembayaran akan ditampilkan di sini setelah pengurus menyelesaikan pengaturan.
      </div>
    );
  }

  const openWhatsApp = () => {
    const lines = [
      "Assalamu'alaikum, saya ingin mengonfirmasi infak untuk ruang baca dan kebun mini Khairul Falah.",
      "",
      `Nama (opsional): ${waName || "-"}`,
      `Nominal: ${waAmount ? "Rp" + Number(waAmount).toLocaleString("id-ID") : "Rp..."}`,
      `Tanggal transfer: ${waDate || "-"}`,
      `Metode: ${waMethod}`,
      "Bukti pembayaran akan saya lampirkan.",
    ];
    const url = `https://wa.me/${settings.pic_whatsapp}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="mt-2" data-testid="payment-section">
      <h3 className="text-xl font-bold text-[#1B4D3E] mb-3 flex items-center gap-2">
        <Landmark size={18} /> Cara Berinfak
      </h3>

      {hasInstructions && (
        <div className="p-4 rounded-lg bg-white border border-[#E2E8F0] mb-3 text-sm text-[#1E293B] whitespace-pre-wrap" data-testid="payment-instructions">
          {settings.payment_instructions}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {hasBank && (
          <div className="p-4 rounded-lg bg-[#EAF2EF] border border-[#1B4D3E]/10" data-testid="payment-bank">
            <div className="flex items-center gap-2 text-[#1B4D3E] font-semibold mb-2">
              <Landmark size={16} /> Transfer Bank / Tunai
            </div>
            <p className="text-sm text-[#1E293B] whitespace-pre-wrap">{settings.bank_info}</p>
          </div>
        )}

        {hasQris && (
          <div className="p-4 rounded-lg bg-white border border-[#C59B27]/30" data-testid="payment-qris">
            <div className="flex items-center gap-2 text-[#1B4D3E] font-semibold mb-2">
              <QrCode size={16} /> QRIS
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setZoom(true)}
                className="shrink-0 bg-white rounded-lg p-1 border border-[#E2E8F0] hover:border-[#1B4D3E]"
                data-testid="qris-thumb-btn">
                <img src={publicFileUrl(settings.qris_file_id)} alt="QRIS"
                  className="h-32 w-32 sm:h-40 sm:w-40 object-contain" />
              </button>
              <div className="flex-1 flex flex-col justify-between">
                {settings.qris_merchant_name && (
                  <div>
                    <div className="text-xs text-[#64748B]">Penerima</div>
                    <div className="font-semibold text-[#1B4D3E] text-sm">{settings.qris_merchant_name}</div>
                  </div>
                )}
                <div className="flex flex-col gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => setZoom(true)} data-testid="qris-zoom-btn">
                    <Maximize2 size={12} className="mr-1" /> Perbesar QRIS
                  </Button>
                  <Button size="sm" variant="outline" asChild data-testid="qris-download-btn">
                    <a href={publicFileUrl(settings.qris_file_id)} download={`QRIS-${(settings.qris_merchant_name || "infak").replace(/\s+/g, "-")}.png`}>
                      <Download size={12} className="mr-1" /> Unduh QRIS
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {hasWa && (
        <div className="mt-4 p-4 rounded-lg bg-[#25D366]/10 border border-[#25D366]/30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="whatsapp-cta">
          <div>
            <div className="font-semibold text-[#1B4D3E] mb-1">Setelah transfer, konfirmasi via WhatsApp</div>
            <p className="text-xs text-[#64748B]">
              Lampirkan bukti pembayaran saat kirim ke pengurus{settings.pic_name ? ` (${settings.pic_name})` : ""}.
            </p>
          </div>
          <Button onClick={() => setWaOpen(true)} className="bg-[#25D366] hover:bg-[#1EBE57] text-white shrink-0" data-testid="confirm-wa-btn">
            <MessageCircle size={14} className="mr-2" /> Konfirmasi Infak via WhatsApp
          </Button>
        </div>
      )}

      <p className="text-xs text-[#94A3B8] mt-3">
        Pembayaran tidak otomatis masuk saldo. Bendahara akan memverifikasi bukti sebelum mencatatnya.
      </p>

      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>QRIS {settings.qris_merchant_name ? `— ${settings.qris_merchant_name}` : ""}</DialogTitle></DialogHeader>
          <div className="flex justify-center bg-white p-2">
            <img src={publicFileUrl(settings.qris_file_id)} alt="QRIS besar"
              className="w-full h-auto max-h-[70vh] object-contain" data-testid="qris-zoom-img" />
          </div>
          <DialogFooter>
            <Button variant="outline" asChild>
              <a href={publicFileUrl(settings.qris_file_id)} download>
                <Download size={14} className="mr-1" /> Unduh
              </a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Konfirmasi Pembayaran via WhatsApp</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#64748B]">
              Isi detail pembayaran. WhatsApp akan terbuka dengan pesan terisi — Anda dapat mengedit, melampirkan bukti, lalu mengirim sendiri.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nama (opsional)</Label>
                <Input value={waName} onChange={(e) => setWaName(e.target.value)} data-testid="wa-name-input" />
              </div>
              <div>
                <Label className="text-xs">Nominal (Rp)</Label>
                <Input type="number" min="1" value={waAmount} onChange={(e) => setWaAmount(e.target.value)} data-testid="wa-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Tanggal Transfer</Label>
                <Input type="date" value={waDate} onChange={(e) => setWaDate(e.target.value)} data-testid="wa-date-input" />
              </div>
              <div>
                <Label className="text-xs">Metode</Label>
                <select value={waMethod} onChange={(e) => setWaMethod(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm"
                  data-testid="wa-method-select">
                  <option value="QRIS">QRIS</option>
                  <option value="Transfer bank">Transfer bank</option>
                  <option value="Tunai">Tunai</option>
                </select>
              </div>
            </div>
            <p className="text-xs text-[#94A3B8]">Pesan tidak dikirim otomatis. Setelah WhatsApp terbuka, lampirkan bukti pembayaran lalu tekan kirim.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaOpen(false)}>Batal</Button>
            <Button onClick={openWhatsApp} className="bg-[#25D366] hover:bg-[#1EBE57] text-white" data-testid="wa-open-btn">
              <MessageCircle size={14} className="mr-2" /> Buka WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
